import React from 'react';
import { Icon, IconName } from '@cotera/client/app/components/ui/icon';
import { DG } from '@cotera/sdk/core';
import { iconForGraphColumn } from '../../data-grid/utils';
import { ActivityCard } from './activity-card';

const formatRelativeTime = (timestamp: Date): string => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks}w ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  return `${diffInMonths}mo ago`;
};

type RecentActivity = {
  type: 'dataset' | 'column' | 'agent' | 'document';
  datasetName?: string;
  columnName?: string;
  name?: string; // For agents and documents
  timestamp: Date;
  icon: IconName;
};

type StoredActivity = {
  type: 'dataset' | 'column' | 'agent' | 'document';
  datasetName?: string;
  columnName?: string;
  name?: string; // For agents and documents
  timestamp: string; // ISO string for JSON serialization
};

const RECENT_ACTIVITY_KEY_PREFIX = 'cotera_recent_activity';
const MAX_STORED_ITEMS = 50;
const RECENT_ACTIVITY_EVENT = 'cotera_recent_activity_updated';

const getRecentActivityKey = (orgId: string) =>
  `${RECENT_ACTIVITY_KEY_PREFIX}_${orgId}`;

const getRecentActivityFromStorage = (
  graph: DG.BuildExplorer,
  orgId: string
): RecentActivity[] => {
  try {
    const stored = localStorage.getItem(getRecentActivityKey(orgId));
    if (stored === null) {
      return [];
    }

    const items: StoredActivity[] = JSON.parse(stored);
    return items
      .filter((item) => {
        // For datasets and columns, check if the dataset exists
        if (item.type === 'dataset' || item.type === 'column') {
          if (
            item.datasetName === undefined ||
            !graph.hasSymbol(item.datasetName)
          ) {
            return false;
          }

          // If it's a column, check if the column exists
          if (item.type === 'column' && item.columnName !== undefined) {
            const column = graph.getColumn({
              rel: item.datasetName,
              name: item.columnName,
            });
            return column !== null;
          }

          return true;
        }

        // For agents and documents, check if they exist in the graph
        if (item.type === 'agent' || item.type === 'document') {
          if (item.name === undefined) {
            return false;
          }
          return graph.hasSymbol(item.name);
        }

        return false;
      })
      .map((item) => ({
        type: item.type,
        datasetName: item.datasetName,
        columnName: item.columnName,
        name: item.name,
        timestamp: new Date(item.timestamp),
        icon:
          item.type === 'column' &&
          item.columnName !== undefined &&
          item.datasetName !== undefined
            ? (iconForGraphColumn(graph, {
                rel: item.datasetName,
                name: item.columnName,
              }) as IconName)
            : item.type === 'agent'
            ? ('sparkles' as const)
            : item.type === 'document'
            ? ('document-text' as const)
            : ('table' as const),
      }))
      .slice(0, 5); // Show top 5
  } catch {
    return [];
  }
};

export const trackRecentActivity = (
  type: 'dataset' | 'column' | 'agent' | 'document',
  nameOrDataset: string,
  orgId: string,
  columnName?: string
) => {
  try {
    const recentActivityKey = getRecentActivityKey(orgId);
    const stored = localStorage.getItem(recentActivityKey);
    const items: StoredActivity[] = stored !== null ? JSON.parse(stored) : [];

    // Create unique key for deduplication
    let key: string;
    if (type === 'column' && columnName !== undefined) {
      key = `column:${nameOrDataset}/${columnName}`;
    } else if (type === 'dataset') {
      key = `dataset:${nameOrDataset}`;
    } else {
      // agent or document
      key = `${type}:${nameOrDataset}`;
    }

    // Remove duplicate if it exists
    const filtered = items.filter((item) => {
      let itemKey: string;
      if (
        item.type === 'column' &&
        item.columnName !== undefined &&
        item.datasetName !== undefined
      ) {
        itemKey = `column:${item.datasetName}/${item.columnName}`;
      } else if (item.type === 'dataset' && item.datasetName !== undefined) {
        itemKey = `dataset:${item.datasetName}`;
      } else if (
        (item.type === 'agent' || item.type === 'document') &&
        item.name !== undefined
      ) {
        itemKey = `${item.type}:${item.name}`;
      } else {
        return true; // Keep malformed items for now, they'll be filtered on load
      }
      return itemKey !== key;
    });

    // Add new item at the beginning
    const newItem: StoredActivity = {
      type,
      timestamp: new Date().toISOString(),
    };

    if (type === 'agent' || type === 'document') {
      newItem.name = nameOrDataset;
    } else {
      newItem.datasetName = nameOrDataset;
      newItem.columnName = columnName;
    }

    filtered.unshift(newItem);

    // Keep only the most recent items
    const trimmed = filtered.slice(0, MAX_STORED_ITEMS);

    localStorage.setItem(recentActivityKey, JSON.stringify(trimmed));

    // Notify current tab about the update
    window.dispatchEvent(new Event(RECENT_ACTIVITY_EVENT));
  } catch {
    // Silently fail if tracking fails
  }
};

// Custom hook for recent activity
const useRecentActivity = (graph: DG.BuildExplorer, orgId: string) => {
  const recentActivityKey = getRecentActivityKey(orgId);
  const [activities, setActivities] = React.useState(() =>
    getRecentActivityFromStorage(graph, orgId)
  );

  React.useEffect(() => {
    const updateActivities = () => {
      setActivities(getRecentActivityFromStorage(graph, orgId));
    };

    // Listen for updates from current tab
    window.addEventListener(RECENT_ACTIVITY_EVENT, updateActivities);

    // Listen for updates from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === recentActivityKey) {
        updateActivities();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener(RECENT_ACTIVITY_EVENT, updateActivities);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [graph, orgId, recentActivityKey]);

  return activities;
};

export type RecentActivitySectionProps = {
  graph: DG.BuildExplorer;
  orgId: string;
  onActivityClick: (activity: RecentActivity) => void;
};

export const RecentActivitySection: React.FC<RecentActivitySectionProps> = ({
  graph,
  orgId,
  onActivityClick,
}) => {
  const recentActivity = useRecentActivity(graph, orgId);
  const [showLeftMask, setShowLeftMask] = React.useState(false);
  const [showRightMask, setShowRightMask] = React.useState(false);
  const [isHovering, setIsHovering] = React.useState(false);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Handle scroll to update mask visibility
  const handleScroll = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (container === null) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftMask(scrollLeft > 0);
    setShowRightMask(scrollLeft < scrollWidth - clientWidth);
  }, []);

  // Scroll in the specified direction
  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (container === null) {
      return;
    }
    container.scrollBy({
      left: direction === 'left' ? -280 : 280,
      behavior: 'smooth',
    });
  };

  // Set up scroll listener for mask visibility
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (container === null) {
      return;
    }

    // Check initial state
    handleScroll();

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, recentActivity]);

  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-4">
        <Icon icon="clock" className="w-4 h-4 text-slate-900" />
        <h2 className="text-xl font-semibold text-slate-900">
          Recent activity
        </h2>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Items you've recently accessed
      </p>

      {recentActivity.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border border-slate-200 rounded-lg bg-slate-50">
          <p>Your recently accessed items will appear here</p>
        </div>
      ) : (
        <div
          className="relative -mx-1"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {/* Left fade mask - only show when scrolled */}
          {showLeftMask && (
            <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent pointer-events-none z-10" />
          )}

          {/* Right fade mask - only show when there's more content */}
          {showRightMask && (
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent pointer-events-none z-10" />
          )}

          {/* Left navigation button */}
          {showLeftMask && isHovering && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center bg-white border border-slate-200 rounded-full shadow-sm hover:bg-slate-50 hover:border-slate-300 transition-colors"
              aria-label="Scroll left"
            >
              <Icon icon="chevron-left" className="w-4 h-4 text-slate-700" />
            </button>
          )}

          {/* Right navigation button */}
          {showRightMask && isHovering && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center bg-white border border-slate-200 rounded-full shadow-sm hover:bg-slate-50 hover:border-slate-300 transition-colors"
              aria-label="Scroll right"
            >
              <Icon icon="chevron-right" className="w-4 h-4 text-slate-700" />
            </button>
          )}

          <div
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto pb-2 px-1"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            <style>{`
              .flex.gap-4.overflow-x-auto::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            {recentActivity.map((activity, index) => {
              let title: string;
              let subtitle: string | undefined;

              if (
                activity.type === 'column' &&
                activity.columnName !== undefined
              ) {
                title = activity.columnName;
                subtitle = activity.datasetName;
              } else if (
                activity.type === 'dataset' &&
                activity.datasetName !== undefined
              ) {
                title = activity.datasetName;
                subtitle = undefined;
              } else if (
                (activity.type === 'agent' || activity.type === 'document') &&
                activity.name !== undefined
              ) {
                title = activity.name;
                subtitle = undefined;
              } else {
                title = 'Unknown';
                subtitle = undefined;
              }

              return (
                <ActivityCard
                  key={index}
                  icon={activity.icon}
                  title={title}
                  subtitle={subtitle}
                  timestamp={formatRelativeTime(activity.timestamp)}
                  onClick={() => onActivityClick(activity)}
                  className="flex-shrink-0 w-64"
                />
              );
            })}
            {/* Spacer for scroll padding on the right */}
            <div className="flex-shrink-0 w-px" />
          </div>
        </div>
      )}
    </div>
  );
};

export type { RecentActivity };
